import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { api } from "@/lib/api";
import { ChevronLeft } from "lucide-react";

export default function NewsPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/news/${slug}`).then((r) => setPost(r.data)).catch(() => setError(true));
  }, [slug]);

  if (error)
    return (
      <div className="wyv-container py-20">
        <Card className="max-w-lg"><CardContent className="p-8">
          <h1 className="font-heading font-semibold text-xl mb-2">Story not found</h1>
          <Button asChild variant="outline" className="mt-3"><Link to="/news">Back to news</Link></Button>
        </CardContent></Card>
      </div>
    );

  if (!post) return <div className="wyv-container py-12"><Skeleton className="h-96 max-w-3xl mx-auto" /></div>;

  return (
    <div className="wyv-container py-12 sm:py-16">
      <article className="max-w-3xl mx-auto">
        <Link to="/news" className="inline-flex items-center text-sm font-medium text-primary hover:underline mb-6">
          <ChevronLeft className="h-4 w-4 mr-0.5" /> All news
        </Link>
        <p className="text-xs font-semibold uppercase tracking-wider text-accent mb-3">
          {post.published_at ? new Date(post.published_at).toLocaleDateString("en-CA", { month: "long", day: "numeric", year: "numeric" }) : ""}
          {post.author && <span className="text-slate-400 ml-2 normal-case tracking-normal">· {post.author}</span>}
        </p>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground leading-tight" data-testid="news-post-title">
          {post.title}
        </h1>
        {post.summary && <p className="text-base text-muted-foreground leading-relaxed mt-4">{post.summary}</p>}
        {post.image && <img src={post.image} alt="" className="w-full rounded-xl border border-slate-200 mt-8" />}
        {post.body && (
          <div className="mt-8 text-[15px] text-foreground leading-relaxed whitespace-pre-line" data-testid="news-post-body">
            {post.body}
          </div>
        )}
        {post.ctas?.length > 0 && (
          <div className="flex flex-wrap gap-3 mt-8" data-testid="news-post-ctas">
            {post.ctas.map((cta, i) => (
              <Button key={cta.to} asChild
                      className={i === 0 ? "bg-emerald-600 hover:bg-emerald-700 text-white" : ""}
                      variant={i === 0 ? "default" : "outline"}
                      data-testid={`news-post-cta-${i}`}>
                <Link to={cta.to}>{cta.label}</Link>
              </Button>
            ))}
          </div>
        )}
      </article>
    </div>
  );
}
